import { compileExpression as filtrexCompile } from 'filtrex';

// filtrex wrapper, helpers (round, clamp, min, max, map)

export function mapLookup(val: any, def: any, ...pairs: any[]) {
  const strVal = val === null || val === undefined ? '' : String(val);
  for (let i = 0; i < pairs.length; i += 2) {
    const key = pairs[i];
    const mapVal = pairs[i + 1];
    if (String(key) === strVal) {
      return mapVal;
    }
  }
  return def !== undefined ? def : 0;
}

export const standardHelpers = {
  str: (...args: any[]) => args.join(''),
  format_number: (val: any, decimals?: number) => {
    const num = Number(val);
    if (isNaN(num)) return String(val ?? '');
    if (typeof decimals === 'number') {
      return num.toLocaleString('ru-RU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    }
    return num.toLocaleString('ru-RU');
  },
  format: (val: any, decimals?: number) => {
    const num = Number(val);
    if (isNaN(num)) return String(val ?? '');
    if (typeof decimals === 'number') {
      return num.toLocaleString('ru-RU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    }
    return num.toLocaleString('ru-RU');
  },
  round: (val: number, decimals: number = 0) => {
    const factor = Math.pow(10, decimals);
    return Math.round(val * factor) / factor;
  },
  clamp: (val: number, min: number, max: number) => Math.min(Math.max(val, min), max),
  min: (...args: number[]) => Math.min(...args),
  max: (...args: number[]) => Math.max(...args),
  abs: (val: number) => Math.abs(val),
  ceil: (val: number) => Math.ceil(val),
  floor: (val: number) => Math.floor(val),
  coalesce: (...args: any[]) => args.find((arg) => arg !== undefined && arg !== null) ?? 0,
  // usage: map_lookup(val, defaultValue, "key1", val1, "key2", val2, ...)
  map_lookup: mapLookup,
  map: (val: any, defOrObj: any, maybeDef?: any) => {
    // Also support calling map(val, { a: 1 }, def) directly if evaluated in JS
    if (typeof defOrObj === 'object' && defOrObj !== null) {
      const strVal = val === null || val === undefined ? '' : String(val);
      if (strVal in defOrObj) {
        return defOrObj[strVal];
      }
      return maybeDef !== undefined ? maybeDef : 0;
    }
    return maybeDef !== undefined ? maybeDef : 0;
  },
};

export function preprocessFormula(expr: string): string {
  if (!expr || typeof expr !== 'string') return expr;
  // Replace newlines with spaces to allow multiline formulas/expressions
  expr = expr.replace(/\r?\n/g, ' ');
  let result = '';
  let i = 0;
  while (i < expr.length) {
    if (expr.startsWith('map', i) && (i === 0 || !/[a-zA-Z0-9_]/.test(expr[i - 1]))) {
      let openParenIdx = i + 3;
      while (openParenIdx < expr.length && /\s/.test(expr[openParenIdx])) {
        openParenIdx++;
      }
      if (expr[openParenIdx] === '(') {
        let depth = 1;
        let p = openParenIdx + 1;
        let inSingleQuote = false;
        let inDoubleQuote = false;
        let openBraceIdx = -1;
        let closeBraceIdx = -1;

        while (p < expr.length && depth > 0) {
          const char = expr[p];
          if (char === "'" && !inDoubleQuote) {
            inSingleQuote = !inSingleQuote;
          } else if (char === '"' && !inSingleQuote) {
            inDoubleQuote = !inDoubleQuote;
          } else if (!inSingleQuote && !inDoubleQuote) {
            if (char === '{' && openBraceIdx === -1) {
              openBraceIdx = p;
            } else if (char === '}' && openBraceIdx !== -1 && closeBraceIdx === -1) {
              closeBraceIdx = p;
            } else if (char === '(') {
              depth++;
            } else if (char === ')') {
              depth--;
              if (depth === 0) {
                break;
              }
            }
          }
          p++;
        }

        if (depth === 0 && openBraceIdx !== -1 && closeBraceIdx !== -1) {
          const beforeBrace = expr.slice(openParenIdx + 1, openBraceIdx);
          const valCommaIdx = beforeBrace.lastIndexOf(',');
          if (valCommaIdx !== -1) {
            const valExpr = beforeBrace.slice(0, valCommaIdx).trim();
            const mapBody = expr.slice(openBraceIdx + 1, closeBraceIdx);
            const afterBrace = expr.slice(closeBraceIdx + 1, p).trim();
            let defaultExpr = '0';
            if (afterBrace.startsWith(',')) {
              defaultExpr = afterBrace.slice(1).trim() || '0';
            }

            // Parse entries in mapBody
            const pairs: string[] = [];
            let inEntryQuote = false;
            let quoteChar = '';
            let currentEntry = '';
            for (let c = 0; c < mapBody.length; c++) {
              const ch = mapBody[c];
              if (!inEntryQuote && (ch === '"' || ch === "'")) {
                inEntryQuote = true;
                quoteChar = ch;
                currentEntry += ch;
              } else if (inEntryQuote && ch === quoteChar) {
                inEntryQuote = false;
                currentEntry += ch;
              } else if (ch === ',' && !inEntryQuote) {
                const colon = currentEntry.indexOf(':');
                if (colon !== -1) {
                  let k = currentEntry.slice(0, colon).trim();
                  const v = currentEntry.slice(colon + 1).trim();
                  if ((k.startsWith('"') && k.endsWith('"')) || (k.startsWith("'") && k.endsWith("'"))) {
                    k = k.slice(1, -1);
                  }
                  pairs.push(JSON.stringify(k), preprocessFormula(v));
                }
                currentEntry = '';
              } else {
                currentEntry += ch;
              }
            }
            if (currentEntry.trim()) {
              const colon = currentEntry.indexOf(':');
              if (colon !== -1) {
                let k = currentEntry.slice(0, colon).trim();
                const v = currentEntry.slice(colon + 1).trim();
                if ((k.startsWith('"') && k.endsWith('"')) || (k.startsWith("'") && k.endsWith("'"))) {
                  k = k.slice(1, -1);
                }
                pairs.push(JSON.stringify(k), preprocessFormula(v));
              }
            }

            const transformed = 'map_lookup(' +
              preprocessFormula(valExpr) + ', ' +
              preprocessFormula(defaultExpr) +
              (pairs.length ? ', ' + pairs.join(', ') : '') +
              ')';

            result += transformed;
            i = p + 1;
            continue;
          }
        }
      }
    }
    result += expr[i];
    i++;
  }
  return result;
}

export interface CompiledExpression {
  (context: Record<string, any>): any;
}

export function compileExpression(
  expression: string,
  customFunctions?: Record<string, (...args: any[]) => any>,
  options?: { customProp?: (name: string, get: (name: string) => any, obj: any, type: string) => any }
): CompiledExpression {
  const trimmed = expression?.trim();
  if (!trimmed) {
    return () => undefined;
  }

  const preprocessed = preprocessFormula(trimmed);

  const extraFunctions = {
    ...standardHelpers,
    ...customFunctions,
  };

  const filtrexOptions: any = { extraFunctions };
  if (options?.customProp) {
    filtrexOptions.customProp = options.customProp;
  }

  const compiled = filtrexCompile(preprocessed, filtrexOptions);

  return (context: Record<string, any>) => {
    return compiled(context ?? {});
  };
}

export function evaluateExpression(
  expression: string,
  context: Record<string, any> = {},
  customFunctions?: Record<string, (...args: any[]) => any>
): any {
  const compiled = compileExpression(expression, customFunctions);
  return compiled(context);
}

